import { RegistrationData } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.example.com';
const TAX_CONSULTANT_ENDPOINT = process.env.REACT_APP_TAX_CONSULTANT_ENDPOINT || '/tax_consultants';
const TRACKING_ENDPOINT = process.env.REACT_APP_TRACKING_ENDPOINT || '/tax_consultants/track';
const BEARER_TOKEN = process.env.REACT_APP_BEARER_TOKEN;

export interface TaxConsultantPayload {
  firm_name: string;
  license_number: string;
  phone_number: string;
  email_address: string;
  address: string;
  cac_number: string;
  nin?: string;
  tin?: string;
  years_in_practice: string;
  professional_body: string;
  membership_number: string;
  partners: string; // JSON string of partners array
  files: File;
}

export type TaxIdLookupType = 'individual' | 'corporate';

export interface TaxIdLookupResponse {
  sessionId: string;
  emailMasked: string;
  prefill: {
    name: string;
    email: string;
  };
}

export interface OtpVerifyResponse {
  ok: boolean;
  consultantId?: string;
  error?: string;
  taxIdLookup?: {
    name?: string;
    email?: string;
    phone?: string;
    businessAddressDetails?: string;
    tin?: string;
    taxId?: string;
  };
}

export interface DocumentUploadParams {
  taxId: string;
  ownerTypeName: string;
  uploadContextName: string;
  titles: string[];
  descriptions?: string[];
  files: File[];
}

export interface RegistrationSubmitPayload {
  sessionId: string;
  email: string;
  phone?: string;
  professionalLicenseNo: string;
  yearsInPractice: number;
  professionalBody: string;
  membershipNo: string;
  businessAddr: string;
  documentsPrefix?: string;
  firmName: string;
  cacNumber: string;
  partners?: Array<{
    membershipNo: string;
    email: string;
    phone: string;
    yearsInPractice: number;
    professionalBody: string;
  }>;
  documents?: Record<string, string[]>;
}

export interface RegistrationSubmitResponse {
  ok: boolean;
  summary: {
    taxId?: string | null;
    name?: string | null;
    email?: string | null;
    phone?: string | null;
    firmName?: string | null;
    [k: string]: unknown;
  };
}

export interface RegistrationTrackResponse {
  ok: boolean;
  practice: 'INDIVIDUAL' | 'CORPORATE' | string;
  status: string;
  taxId: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  firmName: string | null;
  createdAt: string;
  updatedAt: string;
  error?: string;
}

export interface ConsultantMeResponse<T = any> {
  ok: boolean;
  data: T;
  error?: string;
}

export interface ConsultantClientTokenResponse {
  ok: boolean;
  token?: string;
  error?: string;
  detail?: string;
}

export type TeamRole = 'OWNER' | 'ADMIN' | 'LEAD' | 'MEMBER' | 'VIEWER';
export type TeamClientRole = 'LEAD' | 'CONTRIBUTOR';

export interface TeamSummary {
  id: string;
  name: string;
  description: string | null;
  isProtected?: boolean;
  membersCount: number;
  clientsCount: number;
  createdAt: string;
}

export interface TeamMemberDto {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: TeamRole;
  status: 'INVITED' | 'ACTIVE' | 'REMOVED' | string;
  invitedAt: string | null;
  joinedAt: string | null;
}

export type AddTeamMemberPayload =
  | { memberId: string; role?: TeamRole }
  | { email: string; firstName?: string; lastName?: string; role?: TeamRole };

export type UpdateTeamMemberPayload = {
  firstName?: string | null;
  lastName?: string | null;
  role?: TeamRole;
};

export interface TeamClientAssignmentDto {
  id: string;
  role: TeamClientRole;
  createdAt: string;
  client: {
    id: string;
    companyName: string | null;
    email: string | null;
    tin: string | null;
    cacNumber: string | null;
  };
}

export interface NotificationDto {
  id: string;
  type: string;
  payload: any;
  deliveredAt: string | null;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationsPagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface NotificationsListResponse {
  ok: boolean;
  data: NotificationDto[];
  pagination?: NotificationsPagination;
  unreadCount?: number;
}

export type RequestStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'REJECTED'
  | 'CANCELLED'
  | 'DISENGAGED'
  | 'EXPIRED';

export interface ClientRequestDto {
  id: string;
  consultantCode: string;
  consultantTaxId?: string | null;
  consultantId?: string | null;
  taxId: string;
  requestorEmail?: string | null;
  requesterInfo?: any;
  expectedDeadline?: string | null;
  disengageAt?: string | null;
  message?: string | null;
  status: RequestStatus;
  createdAt: string;
  updatedAt: string;
}

export interface ListRequestsResponse {
  ok: boolean;
  data: ClientRequestDto[];
}

export type ClientStatus = 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';

export type EngagementStatus = 'OPEN' | 'CLOSED';

export interface EngagementSummaryDto {
  id: string;
  title: string;
  status: EngagementStatus;
}

export interface ClientListItemDto {
  id: string;
  email: string | null;
  phone: string | null;
  companyName: string | null;
  tin: string | null;
  cacNumber: string | null;
  accountingYear: string | null;
  status: ClientStatus;
  createdAt: string;
  industry: string | null;
  engagements: EngagementSummaryDto[];
}

export interface ClientsStatsDto {
  totalClients: number;
  totalActiveClients: number;
  uniqueIndustries: number;
  totalFiltered?: number;
}

export interface ClientsPaginationDto {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface ListClientsResponse {
  ok: boolean;
  data: ClientListItemDto[];
  pagination: ClientsPaginationDto;
  stats: ClientsStatsDto;
}

export interface ClientEngagementDto {
  id: string;
  title: string;
  type: 'TAX_FILING' | 'TAX_ASSESSMENT' | 'COMPLIANCE_REVIEW' | 'GENERAL_CONSULTATION';
  priority: 'LOW' | 'MEDIUM' | 'HIGH';
  status: EngagementStatus;
  startDate: string | null;
  dueDate: string | null;
}

export interface ClientDetailDto {
  id: string;
  consultantId: string;
  requestId: string | null;
  clientInfo: any;
  email: string | null;
  phone: string | null;
  address: string | null;
  companyName: string | null;
  cacNumber: string | null;
  tin: string | null;
  registrationDate: string | null;
  accountingYear: string | null;
  industry: string | null;
  status: ClientStatus;
  createdAt: string;
  updatedAt: string;
  engagements: ClientEngagementDto[];
}

export class ApiService {
  private static baseUrl = API_BASE_URL;

  // Convert technical errors to user-friendly messages
  private static getUserFriendlyError(error: any): string {
    // Network-related errors
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      return 'Registration Failed. Please check your internet connection and try again.';
    }
    
    if (error.name === 'TypeError' && error.message.includes('NetworkError')) {
      return 'Network error occurred. Please check your internet connection and try again.';
    }

    // Timeout errors
    if (error.name === 'AbortError' || error.message.includes('timeout')) {
      return 'The request is taking too long. Please check your connection and try again.';
    }

    // HTTP status-based errors
    if (error.message.includes('HTTP error!')) {
      const statusMatch = error.message.match(/status: (\d+)/);
      if (statusMatch) {
        const status = parseInt(statusMatch[1]);
        switch (status) {
          case 400:
            return 'Some of the information provided is invalid. Please review your details and try again.';
          case 401:
            return 'Authentication failed. Please refresh the page and try again.';
          case 403:
            return 'You do not have permission to perform this action. Please contact support.';
          case 404:
            return 'The registration service is temporarily unavailable. Please try again later.';
          case 409:
            return 'A tax consultant with this information already exists. Please check your details.';
          case 413:
            return 'The uploaded files are too large. Please reduce file sizes and try again.';
          case 422:
            return 'Some required information is missing or invalid. Please review your form and try again.';
          case 429:
            return 'Too many registration attempts. Please wait a few minutes and try again.';
          case 500:
            return 'Server error occurred. Please try again in a few minutes or contact support.';
          case 502:
          case 503:
          case 504:
            return 'The registration service is temporarily unavailable. Please try again later.';
          default:
            return 'An unexpected error occurred. Please try again or contact support if the problem persists.';
        }
      }
    }

    // Server validation errors (if the API returns specific error messages)
    if (error.message && typeof error.message === 'string') {
      // Check for common validation error patterns
      if (error.message.toLowerCase().includes('email')) {
        return 'Please check your email address and try again.';
      }
      if (error.message.toLowerCase().includes('phone')) {
        return 'Please check your phone number format and try again.';
      }
      if (error.message.toLowerCase().includes('duplicate') || error.message.toLowerCase().includes('already exists')) {
        return 'A tax consultant with this information already exists. Please check your details.';
      }
      if (error.message.toLowerCase().includes('invalid') || error.message.toLowerCase().includes('format')) {
        return 'Some of the information provided is in an invalid format. Please review your details.';
      }
      if (error.message.toLowerCase().includes('required') || error.message.toLowerCase().includes('missing')) {
        return 'Please ensure all required fields are completed and try again.';
      }
    }

    // Generic fallback for unknown errors
    return 'Registration could not be completed at this time. Please try again or contact support if the problem continues.';
  }

  static async createTaxConsultant(data: RegistrationData): Promise<any> {
    try {
      // Check if bearer token is available (optional check - remove if not required)
      if (!BEARER_TOKEN) {
        console.warn('Bearer token not found. API request may fail if authentication is required.');
      }

      // Create FormData for file upload
      const formData = new FormData();

      // Add all form fields
      formData.append('firm_name', data.firm_name);
      formData.append('license_number', data.license_number);
      formData.append('phone_number', data.phone_number);
      formData.append('email_address', data.email_address);
      formData.append('address', data.address);
      formData.append('cac_number', data.cac_number);
      if (data.nin) formData.append('nin', data.nin);
      if (data.tin) formData.append('tin', data.tin);
      formData.append('years_in_practice', data.years_in_practice.toString());
      formData.append('professional_body', data.professional_body);
      formData.append('membership_number', data.membership_number);

      // Convert partners array to JSON string
      const partnersJson = JSON.stringify(data.partners.map(partner => ({
        name: `${partner.membership_number}`, // Using membership_number as name for now
        membership_number: partner.membership_number,
        email: partner.email,
        phone_number: partner.phone_number,
        years_in_practice: parseInt(partner.years_in_practice.toString(), 10), // Ensure it's sent as integer
        professional_body: partner.professional_body
      })));
      formData.append('partners', partnersJson);

      // Add files
      if (data.files && data.files.length > 0) {
        // For multiple files, we'll add the first file as the main file
        // You might need to adjust this based on your API requirements
        formData.append('files', data.files[0]);
      }

      // Add timeout to prevent hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 seconds timeout

      let response;
      try {
        const headers: HeadersInit = {};
        
        // Add Authorization header if bearer token is provided
        if (BEARER_TOKEN) {
          headers['Authorization'] = `Bearer ${BEARER_TOKEN}`;
        }

        response = await fetch(`${this.baseUrl}${TAX_CONSULTANT_ENDPOINT}`, {
          method: 'POST',
          headers,
          body: formData,
          signal: controller.signal,
          // Don't set Content-Type header, let the browser set it for FormData
        });
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        // Immediately convert fetch errors to user-friendly messages
        throw new Error(this.getUserFriendlyError(fetchError));
      }

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating tax consultant:', error);
      
      // Convert technical error to user-friendly message
      const userFriendlyMessage = this.getUserFriendlyError(error);
      throw new Error(userFriendlyMessage);
    }
  }

  static async lookupTaxId(type: TaxIdLookupType, taxId: string): Promise<TaxIdLookupResponse> {
    try {
      const endpoint =
        type === 'corporate'
          ? '/registration/corporate/taxid-lookup'
          : '/registration/individual/taxid-lookup';

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      let response: Response;
      try {
        response = await fetch(`${this.baseUrl}${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ taxId }),
          signal: controller.signal,
        });
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        throw new Error(this.getUserFriendlyError(fetchError));
      }

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const msg = errorData.error || errorData.message || `HTTP error! status: ${response.status}`;

        // Preserve specific backend error so UI can show a targeted message.
        if (
          response.status === 400 &&
          errorData?.error === 'Tax ID verification failed: A consultant with this Tax ID already exists.'
        ) {
          const err = new Error(msg) as any;
          err.code = 'CONSULTANT_EXISTS';
          throw err;
        }

        throw new Error(msg);
      }

      return await response.json();
    } catch (error) {
      console.error('Error looking up tax ID:', error);
      // Pass through known business errors unchanged
      if ((error as any)?.code === 'CONSULTANT_EXISTS') {
        throw error;
      }

      const userFriendlyMessage = this.getUserFriendlyError(error);
      throw new Error(userFriendlyMessage);
    }
  }

  static async verifyOtp(type: TaxIdLookupType, sessionId: string, code: string): Promise<OtpVerifyResponse> {
    try {
      const endpoint =
        type === 'corporate'
          ? '/registration/corporate/otp/verify'
          : '/registration/individual/otp/verify';

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      let response: Response;
      try {
        response = await fetch(`${this.baseUrl}${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId, code }),
          signal: controller.signal,
        });
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        throw new Error(this.getUserFriendlyError(fetchError));
      }

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error verifying OTP:', error);
      const userFriendlyMessage = this.getUserFriendlyError(error);
      throw new Error(userFriendlyMessage);
    }
  }

  static async uploadDocuments(params: DocumentUploadParams): Promise<any> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      const formData = new FormData();
      formData.append('taxId', params.taxId);
      formData.append('ownerTypeName', params.ownerTypeName);
      formData.append('uploadContextName', params.uploadContextName);
      formData.append('titles', JSON.stringify(params.titles));
      if (params.descriptions) formData.append('descriptions', JSON.stringify(params.descriptions));
      for (const f of params.files) formData.append('files', f);

      let response: Response;
      try {
        response = await fetch(`${this.baseUrl}/documents/upload`, {
          method: 'POST',
          body: formData,
          signal: controller.signal,
        });
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        throw new Error(this.getUserFriendlyError(fetchError));
      }

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error uploading documents:', error);
      const userFriendlyMessage = this.getUserFriendlyError(error);
      throw new Error(userFriendlyMessage);
    }
  }

  static async submitRegistration(type: TaxIdLookupType, payload: RegistrationSubmitPayload): Promise<RegistrationSubmitResponse> {
    try {
      const endpoint =
        type === 'corporate'
          ? '/registration/corporate/submit'
          : '/registration/individual/submit';

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      let response: Response;
      try {
        response = await fetch(`${this.baseUrl}${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        throw new Error(this.getUserFriendlyError(fetchError));
      }

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error submitting registration:', error);
      const userFriendlyMessage = this.getUserFriendlyError(error);
      throw new Error(userFriendlyMessage);
    }
  }

  static async trackRegistrationByTaxId(taxId: string): Promise<RegistrationTrackResponse> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      let response: Response;
      try {
        response = await fetch(`${this.baseUrl}/registration/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ taxId }),
          signal: controller.signal,
        });
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        throw new Error(this.getUserFriendlyError(fetchError));
      }

      clearTimeout(timeoutId);

      const json = await response.json().catch(() => ({}));

      if (!response.ok) {
        // backend uses { ok:false, error:'not_found' } on 404
        const msg = json?.error || json?.message || `HTTP error! status: ${response.status}`;
        const err = new Error(msg) as any;
        err.status = response.status;
        err.payload = json;
        throw err;
      }

      return json as RegistrationTrackResponse;
    } catch (error) {
      console.error('Error tracking registration:', error);

      // Preserve not_found so UI can render "not found" state
      if ((error as any)?.status === 404 || (error as any)?.payload?.error === 'not_found') {
        throw error;
      }

      const userFriendlyMessage = this.getUserFriendlyError(error);
      throw new Error(userFriendlyMessage);
    }
  }

  static async getConsultantMe(accessToken: string): Promise<ConsultantMeResponse> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      let response: Response;
      try {
        response = await fetch(`${this.baseUrl}/consultants/me`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          signal: controller.signal,
        });
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        throw new Error(this.getUserFriendlyError(fetchError));
      }

      clearTimeout(timeoutId);

      const json = await response.json().catch(() => ({}));
      if (!response.ok) {
        const msg = json?.error || json?.message || `HTTP error! status: ${response.status}`;
        const err = new Error(msg) as any;
        err.status = response.status;
        err.payload = json;
        throw err;
      }

      return json as ConsultantMeResponse;
    } catch (error) {
      console.error('Error fetching consultant profile:', error);
      const userFriendlyMessage = this.getUserFriendlyError(error);
      throw new Error(userFriendlyMessage);
    }
  }

  static async getClientToken(accessToken: string, clientId: string): Promise<string> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      const res = await fetch(`${this.baseUrl}/consultants/clientToken`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ clientId }),
        signal: controller.signal,
      });

      const json = (await res.json().catch(() => ({}))) as ConsultantClientTokenResponse;
      if (!res.ok) {
        const msg = (json as any)?.error || (json as any)?.message || `HTTP error! status: ${res.status}`;
        const err = new Error(msg) as any;
        err.status = res.status;
        err.payload = json;
        throw err;
      }

      if (!json?.ok || typeof json.token !== 'string' || !json.token) {
        throw new Error('Could not generate client token.');
      }

      return json.token;
    } catch (e) {
      console.error('Error generating client token:', e);
      throw new Error(this.getUserFriendlyError(e));
    } finally {
      clearTimeout(timeoutId);
    }
  }

  static async listTeams(accessToken: string): Promise<TeamSummary[]> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      const res = await fetch(`${this.baseUrl}/teams`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${accessToken}` },
        signal: controller.signal,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = json?.error || json?.message || `HTTP error! status: ${res.status}`;
        const err = new Error(msg) as any;
        err.status = res.status;
        err.payload = json;
        throw err;
      }
      return json as TeamSummary[];
    } catch (e) {
      console.error('Error listing teams:', e);
      throw new Error(this.getUserFriendlyError(e));
    } finally {
      clearTimeout(timeoutId);
    }
  }

  static async createTeam(accessToken: string, input: { name: string; description?: string }): Promise<any> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      const res = await fetch(`${this.baseUrl}/teams`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
        signal: controller.signal,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = json?.error || json?.message || `HTTP error! status: ${res.status}`;
        const err = new Error(msg) as any;
        err.status = res.status;
        err.payload = json;
        throw err;
      }
      return json;
    } catch (e) {
      console.error('Error creating team:', e);
      throw new Error(this.getUserFriendlyError(e));
    } finally {
      clearTimeout(timeoutId);
    }
  }

  static async deleteTeam(accessToken: string, teamId: string): Promise<void> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      const res = await fetch(`${this.baseUrl}/teams/${encodeURIComponent(teamId)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
        signal: controller.signal,
      });

      if (!res.ok && res.status !== 204) {
        const json = await res.json().catch(() => ({}));
        const msg = json?.error || json?.message || `HTTP error! status: ${res.status}`;
        const err = new Error(msg) as any;
        err.status = res.status;
        err.payload = json;
        throw err;
      }
    } catch (e) {
      console.error('Error deleting team:', e);
      throw new Error(this.getUserFriendlyError(e));
    } finally {
      clearTimeout(timeoutId);
    }
  }

  static async listNotifications(
    accessToken: string,
    opts?: { page?: number; limit?: number; unreadOnly?: boolean },
  ): Promise<NotificationsListResponse> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      const params = new URLSearchParams();
      if (opts?.limit) params.set('limit', String(opts.limit));
      if (opts?.page) params.set('page', String(opts.page));
      if (opts?.unreadOnly) params.set('unread', 'true');

      const res = await fetch(
        `${this.baseUrl}/consultants/notifications${params.toString() ? `?${params.toString()}` : ''}`,
        {
          method: 'GET',
          headers: { Authorization: `Bearer ${accessToken}` },
          signal: controller.signal,
        },
      );

      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = json?.error || json?.message || `HTTP error! status: ${res.status}`;
        const err = new Error(msg) as any;
        err.status = res.status;
        err.payload = json;
        throw err;
      }

      return json as NotificationsListResponse;
    } catch (e) {
      console.error('Error listing notifications:', e);
      throw new Error(this.getUserFriendlyError(e));
    } finally {
      clearTimeout(timeoutId);
    }
  }

  static async markNotificationRead(accessToken: string, id: string): Promise<{ ok: boolean; data: NotificationDto }> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      const res = await fetch(`${this.baseUrl}/consultants/notifications/${encodeURIComponent(id)}/read`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${accessToken}` },
        signal: controller.signal,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = json?.error || json?.message || `HTTP error! status: ${res.status}`;
        const err = new Error(msg) as any;
        err.status = res.status;
        err.payload = json;
        throw err;
      }
      return json as { ok: boolean; data: NotificationDto };
    } catch (e) {
      console.error('Error marking notification read:', e);
      throw new Error(this.getUserFriendlyError(e));
    } finally {
      clearTimeout(timeoutId);
    }
  }

  static async markAllNotificationsRead(accessToken: string): Promise<{ ok: boolean }> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      const res = await fetch(`${this.baseUrl}/consultants/notifications/read-all`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${accessToken}` },
        signal: controller.signal,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = json?.error || json?.message || `HTTP error! status: ${res.status}`;
        const err = new Error(msg) as any;
        err.status = res.status;
        err.payload = json;
        throw err;
      }
      return json as { ok: boolean };
    } catch (e) {
      console.error('Error marking all notifications read:', e);
      throw new Error(this.getUserFriendlyError(e));
    } finally {
      clearTimeout(timeoutId);
    }
  }

  static async listRequests(
    accessToken: string,
    input: { role: 'consultant' | 'taxpayer'; status?: RequestStatus },
  ): Promise<ListRequestsResponse> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      const params = new URLSearchParams();
      params.set('role', input.role);
      if (input.status) params.set('status', input.status);

      const res = await fetch(`${this.baseUrl}/requests?${params.toString()}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${accessToken}` },
        signal: controller.signal,
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = json?.error || json?.message || `HTTP error! status: ${res.status}`;
        const err = new Error(msg) as any;
        err.status = res.status;
        err.payload = json;
        throw err;
      }

      return json as ListRequestsResponse;
    } catch (e) {
      console.error('Error listing requests:', e);
      throw new Error(this.getUserFriendlyError(e));
    } finally {
      clearTimeout(timeoutId);
    }
  }

  static async approveRequest(accessToken: string, id: string): Promise<{ ok: boolean; data: ClientRequestDto }> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      const res = await fetch(`${this.baseUrl}/requests/${encodeURIComponent(id)}/approve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
        signal: controller.signal,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = json?.error || json?.message || `HTTP error! status: ${res.status}`;
        const err = new Error(msg) as any;
        err.status = res.status;
        err.payload = json;
        throw err;
      }
      return json as { ok: boolean; data: ClientRequestDto };
    } catch (e) {
      console.error('Error approving request:', e);
      throw new Error(this.getUserFriendlyError(e));
    } finally {
      clearTimeout(timeoutId);
    }
  }

  static async rejectRequest(accessToken: string, id: string): Promise<{ ok: boolean; data: ClientRequestDto }> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      const res = await fetch(`${this.baseUrl}/requests/${encodeURIComponent(id)}/reject`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
        signal: controller.signal,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = json?.error || json?.message || `HTTP error! status: ${res.status}`;
        const err = new Error(msg) as any;
        err.status = res.status;
        err.payload = json;
        throw err;
      }
      return json as { ok: boolean; data: ClientRequestDto };
    } catch (e) {
      console.error('Error rejecting request:', e);
      throw new Error(this.getUserFriendlyError(e));
    } finally {
      clearTimeout(timeoutId);
    }
  }

  static async listClients(
    accessToken: string,
    input?: { q?: string; status?: ClientStatus; page?: number; pageSize?: number },
  ): Promise<ListClientsResponse> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      const params = new URLSearchParams();
      if (input?.q) params.set('q', input.q);
      if (input?.status) params.set('status', input.status);
      if (input?.page) params.set('page', String(input.page));
      if (input?.pageSize) params.set('pageSize', String(input.pageSize));

      const res = await fetch(`${this.baseUrl}/clients${params.toString() ? `?${params.toString()}` : ''}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${accessToken}` },
        signal: controller.signal,
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = json?.error || json?.message || `HTTP error! status: ${res.status}`;
        const err = new Error(msg) as any;
        err.status = res.status;
        err.payload = json;
        throw err;
      }

      return json as ListClientsResponse;
    } catch (e) {
      console.error('Error listing clients:', e);
      throw new Error(this.getUserFriendlyError(e));
    } finally {
      clearTimeout(timeoutId);
    }
  }

  static async getClient(accessToken: string, id: string): Promise<{ ok: boolean; data: ClientDetailDto }> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      const res = await fetch(`${this.baseUrl}/clients/${encodeURIComponent(id)}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${accessToken}` },
        signal: controller.signal,
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = json?.error || json?.message || `HTTP error! status: ${res.status}`;
        const err = new Error(msg) as any;
        err.status = res.status;
        err.payload = json;
        throw err;
      }

      return json as { ok: boolean; data: ClientDetailDto };
    } catch (e) {
      console.error('Error getting client:', e);
      throw new Error(this.getUserFriendlyError(e));
    } finally {
      clearTimeout(timeoutId);
    }
  }

  static async updateClientAccountingYear(
    accessToken: string,
    clientId: string,
    consultantId: string,
    accountingYear: string,
  ): Promise<any> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      const res = await fetch(`${this.baseUrl}/clients/${encodeURIComponent(clientId)}/accounting-year`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ consultantId, accountingYear }),
        signal: controller.signal,
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = json?.error || json?.message || `HTTP error! status: ${res.status}`;
        const err = new Error(msg) as any;
        err.status = res.status;
        err.payload = json;
        throw err;
      }

      return json;
    } catch (e) {
      console.error('Error updating accounting year:', e);
      throw new Error(this.getUserFriendlyError(e));
    } finally {
      clearTimeout(timeoutId);
    }
  }

  static async listTeamMembers(accessToken: string, teamId: string): Promise<TeamMemberDto[]> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      const res = await fetch(`${this.baseUrl}/teams/${encodeURIComponent(teamId)}/members`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${accessToken}` },
        signal: controller.signal,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = json?.error || json?.message || `HTTP error! status: ${res.status}`;
        const err = new Error(msg) as any;
        err.status = res.status;
        err.payload = json;
        throw err;
      }
      return json as TeamMemberDto[];
    } catch (e) {
      console.error('Error listing team members:', e);
      throw new Error(this.getUserFriendlyError(e));
    } finally {
      clearTimeout(timeoutId);
    }
  }

  static async addTeamMember(
    accessToken: string,
    teamId: string,
    input: AddTeamMemberPayload,
  ): Promise<any> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      const res = await fetch(`${this.baseUrl}/teams/${encodeURIComponent(teamId)}/members`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
        signal: controller.signal,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = json?.error || json?.message || `HTTP error! status: ${res.status}`;
        const err = new Error(msg) as any;
        err.status = res.status;
        err.payload = json;
        throw err;
      }
      return json;
    } catch (e) {
      console.error('Error adding team member:', e);
      throw new Error(this.getUserFriendlyError(e));
    } finally {
      clearTimeout(timeoutId);
    }
  }

  static async removeTeamMember(accessToken: string, teamId: string, memberId: string): Promise<void> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      const res = await fetch(
        `${this.baseUrl}/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(memberId)}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${accessToken}` },
          signal: controller.signal,
        },
      );
      if (!res.ok && res.status !== 204) {
        const json = await res.json().catch(() => ({}));
        const msg = json?.error || json?.message || `HTTP error! status: ${res.status}`;
        const err = new Error(msg) as any;
        err.status = res.status;
        err.payload = json;
        throw err;
      }
    } catch (e) {
      console.error('Error removing team member:', e);
      throw new Error(this.getUserFriendlyError(e));
    } finally {
      clearTimeout(timeoutId);
    }
  }

  static async updateTeamMember(
    accessToken: string,
    teamId: string,
    memberId: string,
    input: UpdateTeamMemberPayload,
  ): Promise<TeamMemberDto> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      const res = await fetch(
        `${this.baseUrl}/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(memberId)}`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(input),
          signal: controller.signal,
        },
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = json?.error || json?.message || `HTTP error! status: ${res.status}`;
        const err = new Error(msg) as any;
        err.status = res.status;
        err.payload = json;
        throw err;
      }
      return json as TeamMemberDto;
    } catch (e) {
      console.error('Error updating team member:', e);
      throw new Error(this.getUserFriendlyError(e));
    } finally {
      clearTimeout(timeoutId);
    }
  }

  static async listTeamClients(accessToken: string, teamId: string): Promise<TeamClientAssignmentDto[]> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      const res = await fetch(`${this.baseUrl}/teams/${encodeURIComponent(teamId)}/clients`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${accessToken}` },
        signal: controller.signal,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = json?.error || json?.message || `HTTP error! status: ${res.status}`;
        const err = new Error(msg) as any;
        err.status = res.status;
        err.payload = json;
        throw err;
      }
      return json as TeamClientAssignmentDto[];
    } catch (e) {
      console.error('Error listing team clients:', e);
      throw new Error(this.getUserFriendlyError(e));
    } finally {
      clearTimeout(timeoutId);
    }
  }

  static async assignClientToTeam(
    accessToken: string,
    teamId: string,
    input: { clientId: string; role?: TeamClientRole },
  ): Promise<any> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      const res = await fetch(`${this.baseUrl}/teams/${encodeURIComponent(teamId)}/clients`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
        signal: controller.signal,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = json?.error || json?.message || `HTTP error! status: ${res.status}`;
        const err = new Error(msg) as any;
        err.status = res.status;
        err.payload = json;
        throw err;
      }
      return json;
    } catch (e) {
      console.error('Error assigning client to team:', e);
      throw new Error(this.getUserFriendlyError(e));
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // Alternative method for JSON payload (if API supports it)
  static async createTaxConsultantJson(data: RegistrationData): Promise<any> {
    try {
      // Check if bearer token is available (optional check - remove if not required)
      if (!BEARER_TOKEN) {
        console.warn('Bearer token not found. API request may fail if authentication is required.');
      }

      const payload = {
        firm_name: data.firm_name,
        license_number: data.license_number,
        phone_number: data.phone_number,
        email_address: data.email_address,
        address: data.address,
        cac_number: data.cac_number,
        ...(data.nin ? { nin: data.nin } : {}),
        ...(data.tin ? { tin: data.tin } : {}),
        years_in_practice: data.years_in_practice.toString(),
        professional_body: data.professional_body,
        membership_number: data.membership_number,
        partners: JSON.stringify(data.partners.map(partner => ({
          membership_number: partner.membership_number,
          email: partner.email,
          phone_number: partner.phone_number,
          years_in_practice: parseInt(partner.years_in_practice.toString(), 10), // Ensure it's sent as integer
          professional_body: partner.professional_body
        }))),
        // Note: Files would need to be handled separately for JSON payload
      };

      // Add timeout to prevent hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 seconds timeout

      let response;
      try {
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
        };
        
        // Add Authorization header if bearer token is provided
        if (BEARER_TOKEN) {
          headers['Authorization'] = `Bearer ${BEARER_TOKEN}`;
        }

        response = await fetch(`${this.baseUrl}${TAX_CONSULTANT_ENDPOINT}`, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
          signal: controller.signal,
        });
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        // Immediately convert fetch errors to user-friendly messages
        throw new Error(this.getUserFriendlyError(fetchError));
      }

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating tax consultant:', error);
      
      // Convert technical error to user-friendly message
      const userFriendlyMessage = this.getUserFriendlyError(error);
      throw new Error(userFriendlyMessage);
    }
  }

  // Track tax consultant application by tracking ID
  static async trackApplication(trackingId: string): Promise<any> {
    try {
      // Check if bearer token is available (optional check - remove if not required)
      if (!BEARER_TOKEN) {
        console.warn('Bearer token not found. API request may fail if authentication is required.');
      }

      // Add timeout to prevent hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 seconds timeout

      let response;
      try {
        const headers: HeadersInit = {};
        
        // Add Authorization header if bearer token is provided
        if (BEARER_TOKEN) {
          headers['Authorization'] = `Bearer ${BEARER_TOKEN}`;
        }

        response = await fetch(`${this.baseUrl}${TRACKING_ENDPOINT}${encodeURIComponent(trackingId)}`, {
          method: 'GET',
          headers,
          signal: controller.signal,
        });
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        // Immediately convert fetch errors to user-friendly messages
        throw new Error(this.getUserFriendlyError(fetchError));
      }

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Handle specific 404 error for tracking
        if (response.status === 404) {
          throw new Error('Invalid tracking ID. Please check your tracking ID and try again.');
        }
        
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error tracking application:', error);
      
      // Convert technical error to user-friendly message
      const userFriendlyMessage = this.getUserFriendlyError(error);
      throw new Error(userFriendlyMessage);
    }
  }
}
